/* eslint-disable react/jsx-props-no-spreading */
import { format as formatValue } from '@buttercup/react-formatted-input';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { isMessage } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';

import styles from '../../../styles/fields/text.module.scss';

type FormatRule = {
    char: string | RegExp;
    repeat?: number;
    exactly?: string;
};

interface TextFieldProps {
    name?: string;
    type?: string;
    inputMode?: string;
    value?: string;
    placeholder?: string | MessageDescriptor;
    maxLength?: number;
    pattern?: string;
    format?: FormatRule[];
    disableAutocomplete?: boolean;
    min?: number | string;
    max?: number | string;
    className?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    required?: boolean;
    onChange?: (value: string) => void;
    onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>;
    onClick?: React.MouseEventHandler<HTMLInputElement>;
    [key: string]: any;
}

function TextField({
    name = null,
    type = 'text',
    inputMode = 'text',
    value = null,
    placeholder = null,
    maxLength = null,
    pattern = null,
    format = null,
    min = null,
    max = null,
    required = false,
    disableAutocomplete = false,
    className = null,
    inputRef = null,
    onChange = null,
    onKeyPress = null,
    onClick = null,
    ...props
}: TextFieldProps) {
    const themeClassName = useThemeClassName(styles);
    const intl = useIntl();
    const onInputChange = useCallback(
        ({ target: { value: newValue } }: React.ChangeEvent<HTMLInputElement>) =>
            onChange?.(format !== null ? formatValue(newValue, format).raw : newValue),
        [onChange, format],
    );
    return (
        <input
            {...props}
            className={classNames([styles.container, themeClassName, className])}
            type={type}
            inputMode={inputMode}
            name={name || undefined}
            value={format !== null ? formatValue(value || '', format).formatted : value || ''}
            ref={inputRef}
            placeholder={
                isMessage(placeholder) ? intl.formatMessage(placeholder) : placeholder || undefined
            }
            maxLength={maxLength || undefined}
            pattern={pattern || undefined}
            min={min || undefined}
            max={max || undefined}
            required={required}
            autoComplete={disableAutocomplete ? 'no-autofill' : undefined}
            onChange={onInputChange}
            onKeyPress={onKeyPress}
            onClick={onClick}
        />
    );
}

// eslint-disable-next-line react/display-name
export default React.forwardRef<HTMLInputElement, Omit<TextFieldProps, 'inputRef'>>(
    (props, ref) => <TextField inputRef={ref} {...props} />,
);
